import * as T from 'three';

export const CRYSTAL_LIMB_COUNT=4;
const PAIRS=[['LeftForeArm','LeftHand'],['RightForeArm','RightHand'],['LeftLeg','LeftFoot'],['RightLeg','RightFoot']]as const;
export interface CrystalLimbAnchor {
  position:T.Vector3;
  direction:T.Vector3;
  outward:T.Vector3;
  radius:number;
  length:number;
  valid:boolean;
}

/** Calibrate in the rig's bind pose, then follow its actual forearm/calf bones.
 * Imported bone axes never become assumed game axes; twisting a wrist cannot
 * flip the outside of the pod to the inside of the limb. */
export class CrystalLimbAnchors {
  readonly limbs:CrystalLimbAnchor[]=PAIRS.map(()=>({position:new T.Vector3(),direction:new T.Vector3(),outward:new T.Vector3(),radius:.08,length:.3,valid:false}));
  private readonly bones:(T.Bone|null)[]=PAIRS.map(()=>null);
  private readonly ends:(T.Bone|null)[]=PAIRS.map(()=>null);
  private readonly localOut=PAIRS.map(()=>new T.Vector3());
  private readonly a=new T.Vector3();
  private readonly b=new T.Vector3();
  private readonly matrix=new T.Matrix4();
  private readonly rotation=new T.Quaternion();
  private readonly ray=new T.Raycaster();
  reset():void{for(const limb of this.limbs)limb.valid=false;}
  bind(bones:ReadonlyMap<string,T.Bone>,skeleton:T.Skeleton,meshes:readonly T.Mesh[],scale:number):void{
    for(let i=0;i<PAIRS.length;i++){
      const [from,to]=PAIRS[i],bone=bones.get(from),end=bones.get(to);
      this.bones[i]=bone??null;this.ends[i]=end??null;this.limbs[i].valid=!!bone&&!!end;
      if(!bone||!end)continue;
      const index=skeleton.bones.indexOf(bone),endIndex=skeleton.bones.indexOf(end);
      if(index<0||endIndex<0){this.limbs[i].valid=false;continue;}
      this.matrix.copy(skeleton.boneInverses[index]).invert();this.a.setFromMatrixPosition(this.matrix);
      this.rotation.setFromRotationMatrix(this.matrix.extractRotation(this.matrix)).invert();
      this.matrix.copy(skeleton.boneInverses[endIndex]).invert();this.b.setFromMatrixPosition(this.matrix).sub(this.a).normalize();
      const outward=this.localOut[i];outward.set(Math.sign(this.a.x)||1,0,.6);
      outward.addScaledVector(this.b,-outward.dot(this.b)).normalize().applyQuaternion(this.rotation);
      this.limbs[i].radius=(i<2?.075:.095)*scale;
    }
    this.update();
    // Fit once to the visible garment, including thick boots and bracers.
    for(const limb of this.limbs){
      if(!limb.valid)continue;
      this.ray.ray.direction.copy(limb.outward).negate();this.ray.near=0;this.ray.far=.4*scale;
      // Fit the pod's full footprint, including the cuff / knuckles below its
      // centre. A single centre ray misses the wider wrist and boot rims.
      let radius=.035*scale;
      for(const along of [-.075,0,.075,.15,.225]){
        this.ray.ray.origin.copy(limb.position).addScaledVector(limb.direction,along*scale).addScaledVector(limb.outward,.4*scale);
        const hits:T.Intersection[]=[];for(const mesh of meshes)if(mesh.visible)mesh.raycast(this.ray,hits);
        for(const hit of hits)radius=Math.max(radius,.4*scale-hit.distance);
      }
      limb.radius=T.MathUtils.clamp(radius,.035*scale,.18*scale);
    }
  }
  update():void{
    for(let i=0;i<PAIRS.length;i++){
      const bone=this.bones[i],end=this.ends[i],limb=this.limbs[i];if(!bone||!end||!limb.valid)continue;
      this.a.setFromMatrixPosition(bone.matrixWorld);this.b.setFromMatrixPosition(end.matrixWorld);
      limb.direction.subVectors(this.b,this.a);limb.length=limb.direction.length();limb.direction.normalize();
      limb.position.lerpVectors(this.a,this.b,i<2?.38:.36);
      bone.getWorldQuaternion(this.rotation);limb.outward.copy(this.localOut[i]).applyQuaternion(this.rotation);
      limb.outward.addScaledVector(limb.direction,-limb.outward.dot(limb.direction)).normalize();
    }
  }
}

/** A small outward arc unfolds each pod; the crystal retains its identity and
 * follows the live limb throughout the fold rather than a frozen torso slot. */
export class CrystalLimbMotion {
  readonly positions=PAIRS.map(()=>new T.Vector3());
  readonly rotations=PAIRS.map(()=>new T.Quaternion());
  readonly scales=PAIRS.map(()=>new T.Vector3());
  readonly outward=PAIRS.map(()=>new T.Vector3());
  deployment=0;
  private seeded=false;
  private readonly direction=new T.Vector3();
  private readonly up=new T.Vector3();
  private readonly normal=new T.Vector3();
  private readonly right=new T.Vector3();
  private readonly rear=new T.Vector3();
  private readonly basis=new T.Matrix4();
  update(dt:number,flight:boolean,compact:boolean,torso:T.Quaternion,scale:number,anchors:CrystalLimbAnchors,jumpThrust=0,landingApproach=0,boostSpread=0,verticalExhaust?:T.Vector3,verticalBlend=0):void{
    const target=compact?0:flight?1-.85*T.MathUtils.clamp(landingApproach,0,1):T.MathUtils.clamp(jumpThrust,0,1)*.3;
    if(!this.seeded){this.deployment=target;this.seeded=true;}
    this.deployment+=(target-this.deployment)*(1-Math.exp(-6*Math.max(0,Math.min(Number.isFinite(dt)?dt:0,.1))));
    const k=T.MathUtils.smootherstep(this.deployment,0,1),arc=Math.sin(Math.PI*k);
    const spread=flight&&!compact?k*T.MathUtils.clamp(Number.isFinite(boostSpread)?boostSpread:0,0,1):0;
    this.rear.set(0,-1,0).applyQuaternion(torso);
    for(let i=0;i<PAIRS.length;i++){
      const limb=anchors.limbs[i],arm=i<2;
      if(!limb.valid){this.scales[i].setScalar(0);continue;}
      const size=(arm?.48:.57)*T.MathUtils.clamp(limb.length/((arm?.28:.40)*scale),.82,1.18)*(compact?.82:1);
      this.scales[i].setScalar(size);this.outward[i].copy(limb.outward);
      this.positions[i].copy(limb.position).addScaledVector(limb.outward,limb.radius+((arm?.022:.042)+k*(arm?.123:.083)+arc*.026+spread*.045)*scale);
      this.direction.copy(limb.direction).lerp(this.rear,k*.65).addScaledVector(limb.outward,k*.10+spread*.22).normalize();
      if(verticalExhaust&&flight&&!compact)this.direction.lerp(verticalExhaust,T.MathUtils.clamp(verticalBlend,0,1)).normalize();
      this.up.copy(this.direction).negate();this.normal.copy(limb.outward);
      this.right.crossVectors(this.up,this.normal).normalize();this.normal.crossVectors(this.right,this.up).normalize();
      this.rotations[i].setFromRotationMatrix(this.basis.makeBasis(this.right,this.up,this.normal));
    }
  }
}
